import React, { useState, useMemo } from "react";
import {
  FaSearch,
  FaCheckCircle,
  FaMoneyBillWave,
  FaTrashAlt,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaFileInvoiceDollar,
  FaUserCheck,
} from "react-icons/fa";
import "./InstantLoanReport.css";

// Initial Static Data
const initialLoanData = [
  {
    id: "IL-9011",
    applicantName: "Aman Sharma",
    mobile: "9876543210",
    panNumber: "ABCPS1234F",
    appliedAmount: 50000,
    approvedAmount: 0,
    tenureMonths: 12,
    applyDate: "2026-08-15",
    status: "PENDING", // PENDING -> APPROVED -> DISBURSED
  },
  {
    id: "IL-9012",
    applicantName: "Pooja Verma",
    mobile: "9812345678",
    panNumber: "XYZPV5678G",
    appliedAmount: 100000,
    approvedAmount: 90000,
    tenureMonths: 24,
    applyDate: "2026-08-18",
    status: "APPROVED",
  },
  {
    id: "IL-9013",
    applicantName: "Rohan Gupta",
    mobile: "9923456789",
    panNumber: "LMNRG9012K",
    appliedAmount: 30000,
    approvedAmount: 30000,
    tenureMonths: 6,
    applyDate: "2026-08-20",
    status: "DISBURSED",
  },
  {
    id: "IL-9014",
    applicantName: "Sunita Roy",
    mobile: "9734567890",
    panNumber: "JKLSK3456L",
    appliedAmount: 75000,
    approvedAmount: 0,
    tenureMonths: 18,
    applyDate: "2026-08-22",
    status: "PENDING",
  },
  {
    id: "IL-9015",
    applicantName: "Deepak Kumar",
    mobile: "9645678901",
    panNumber: "MNODK7890P",
    appliedAmount: 120000,
    approvedAmount: 100000,
    tenureMonths: 36,
    applyDate: "2026-08-25",
    status: "APPROVED",
  },
  {
    id: "IL-9016",
    applicantName: "Neha Singh",
    mobile: "9556789012",
    panNumber: "PQNNS2345M",
    appliedAmount: 40000,
    approvedAmount: 0,
    tenureMonths: 12,
    applyDate: "2026-08-28",
    status: "PENDING",
  },
];

const InstantLoanReport = () => {
  const [loans, setLoans] = useState(initialLoanData);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal State Management
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [approveAmountInput, setApproveAmountInput] = useState("");
  const [isDisburseModalOpen, setIsDisburseModalOpen] = useState(false);

  // Search and Filter Logic
  const filteredLoans = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return loans;

    return loans.filter((item) => {
      return (
        item.id.toLowerCase().includes(term) ||
        item.applicantName.toLowerCase().includes(term) ||
        item.mobile.includes(term) ||
        item.panNumber.toLowerCase().includes(term) ||
        item.status.toLowerCase().includes(term)
      );
    });
  }, [loans, searchTerm]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredLoans.length / rowsPerPage) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredLoans.slice(start, start + rowsPerPage);
  }, [filteredLoans, currentPage, rowsPerPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Open Approval Modal
  const handleOpenApproveModal = (loan) => {
    setSelectedLoan(loan);
    setApproveAmountInput(loan.appliedAmount.toString());
    setIsApproveModalOpen(true);
  };

  // Confirm Approval
  const handleConfirmApprove = (e) => {
    e.preventDefault();
    const amount = Number(approveAmountInput);
    if (!amount || amount <= 0) {
      alert("Please enter a valid approved amount.");
      return;
    }

    setLoans((prev) =>
      prev.map((item) =>
        item.id === selectedLoan.id
          ? { ...item, status: "APPROVED", approvedAmount: amount }
          : item
      )
    );

    setIsApproveModalOpen(false);
    setSelectedLoan(null);
  };

  // Open Disbursement Confirmation Modal
  const handleOpenDisburseModal = (loan) => {
    setSelectedLoan(loan);
    setIsDisburseModalOpen(true);
  };

  // Confirm Disbursement
  const handleConfirmDisburse = () => {
    setLoans((prev) =>
      prev.map((item) =>
        item.id === selectedLoan.id
          ? { ...item, status: "DISBURSED" }
          : item
      )
    );
    setIsDisburseModalOpen(false);
    setSelectedLoan(null);
  };

  // Delete Loan Record
  const handleDeleteLoan = (loanId) => {
    if (window.confirm("Are you sure you want to delete this loan application record?")) {
      setLoans((prev) => prev.filter((item) => item.id !== loanId));
    }
  };

  return (
    <div className="ilr-main-container">
      {/* Page Header */}
      <div className="ilr-page-header">
        <div className="ilr-header-info">
          <h2>
            <FaFileInvoiceDollar className="ilr-title-icon" />
            Instant Loan Report
          </h2>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="ilr-card">
        {/* Controls Toolbar */}
        <div className="ilr-toolbar">
          <div className="ilr-search-box">
            <input
              type="text"
              placeholder="Search by loan ID, name, mobile, PAN, status..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="ilr-search-input"
            />
          </div>

          <div className="ilr-rows-selector">
            <label htmlFor="ilr-rows-select">Show:</label>
            <select
              id="ilr-rows-select"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="ilr-select"
            >
              <option value={5}>5 Rows</option>
              <option value={10}>10 Rows</option>
              <option value={25}>25 Rows</option>
            </select>
          </div>
        </div>

        {/* Responsive Table */}
        <div className="ilr-table-responsive">
          <table className="ilr-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Loan ID</th>
                <th>Applicant Details</th>
                <th>PAN</th>
                <th>Applied Amt</th>
                <th>Approved Amt</th>
                <th>Tenure</th>
                <th>Status</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row, index) => (
                  <tr key={row.id}>
                    <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                    <td>
                      <span className="ilr-badge-id">{row.id}</span>
                    </td>
                    <td>
                      <div className="ilr-applicant-cell">
                        <strong>{row.applicantName}</strong>
                        <span>{row.mobile}</span>
                      </div>
                    </td>
                    <td className="ilr-mono">{row.panNumber}</td>
                    <td className="ilr-amount">₹{row.appliedAmount.toLocaleString("en-IN")}</td>
                    <td className="ilr-amount">
                      {row.approvedAmount > 0
                        ? `₹${row.approvedAmount.toLocaleString("en-IN")}`
                        : "—"}
                    </td>
                    <td>{row.tenureMonths} M</td>
                    <td>
                      <span className={`ilr-status-pill ilr-status-${row.status.toLowerCase()}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className="ilr-action-btn-group">
                        {/* Approve Button - Visible when PENDING */}
                        {row.status === "PENDING" && (
                          <button
                            type="button"
                            className="ilr-btn-action ilr-btn-approve"
                            title="Approve Loan"
                            onClick={() => handleOpenApproveModal(row)}
                          >
                            <FaUserCheck />
                            <span>Approve</span>
                          </button>
                        )}

                        {/* Disburse Button - Visible when APPROVED */}
                        {row.status === "APPROVED" && (
                          <button
                            type="button"
                            className="ilr-btn-action ilr-btn-disburse"
                            title="Disburse Loan"
                            onClick={() => handleOpenDisburseModal(row)}
                          >
                            <FaMoneyBillWave />
                            <span>Disburse</span>
                          </button>
                        )}

                        {/* Disbursed Badge - Visible when DISBURSED */}
                        {row.status === "DISBURSED" && (
                          <span className="ilr-disbursed-tag">
                            <FaCheckCircle /> Disbursed
                          </span>
                        )}

                        {/* Delete Action */}
                        <button
                          type="button"
                          className="ilr-btn-action ilr-btn-delete"
                          title="Delete Application"
                          onClick={() => handleDeleteLoan(row.id)}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="ilr-table-empty">
                    No loan applications match your filter query.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="ilr-pagination-wrapper">
          <div className="ilr-pagination-summary">
            Showing{" "}
            {filteredLoans.length === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}{" "}
            to {Math.min(currentPage * rowsPerPage, filteredLoans.length)} of{" "}
            {filteredLoans.length} entries
          </div>

          <div className="ilr-pagination-nav">
            <button
              type="button"
              className="ilr-nav-btn"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <FaChevronLeft />
            </button>
            {/* {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                type="button"
                className={`ilr-nav-page-btn ${currentPage === p ? "active" : ""}`}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </button>
            ))} */}
            <span className="">
              {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="ilr-nav-btn"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* MODAL 1: Loan Approval Modal */}
      {isApproveModalOpen && selectedLoan && (
        <div className="ilr-modal-backdrop" onClick={() => setIsApproveModalOpen(false)}>
          <div className="ilr-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="ilr-modal-head">
              <h3>Approve Loan Application</h3>
              <button
                type="button"
                className="ilr-modal-close-btn"
                onClick={() => setIsApproveModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleConfirmApprove}>
              <div className="ilr-modal-body">
                <div className="ilr-summary-box">
                  <div>
                    <label>Applicant Name</label>
                    <p>{selectedLoan.applicantName}</p>
                  </div>
                  <div>
                    <label>Applied Amount</label>
                    <p className="highlight">
                      ₹{selectedLoan.appliedAmount.toLocaleString("en-IN")}
                    </p>
                  </div>
                </div>

                <div className="ilr-form-group">
                  <label htmlFor="approvedAmountInput">
                    Sanctioned / Approved Amount (₹) *
                  </label>
                  <input
                    id="approvedAmountInput"
                    type="number"
                    min={1}
                    required
                    className="ilr-input"
                    value={approveAmountInput}
                    onChange={(e) => setApproveAmountInput(e.target.value)}
                  />
                  <small>Maximum recommended: Equal to or lower than the applied amount.</small>
                </div>
              </div>

              <div className="ilr-modal-foot">
                <button
                  type="button"
                  className="ilr-btn-secondary"
                  onClick={() => setIsApproveModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="ilr-btn-primary">
                  Approve Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Disbursement Confirmation Modal */}
      {isDisburseModalOpen && selectedLoan && (
        <div className="ilr-modal-backdrop" onClick={() => setIsDisburseModalOpen(false)}>
          <div className="ilr-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="ilr-modal-head">
              <h3>Confirm Loan Disbursement</h3>
              <button
                type="button"
                className="ilr-modal-close-btn"
                onClick={() => setIsDisburseModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="ilr-modal-body">
              <p className="ilr-confirmation-text">
                Are you sure you want to disburse{" "}
                <strong>₹{selectedLoan.approvedAmount.toLocaleString("en-IN")}</strong> to{" "}
                <strong>{selectedLoan.applicantName}</strong>?
              </p>
              <div className="ilr-alert-warning">
                Note: Once disbursed, this loan status will permanently update to "DISBURSED".
              </div>
            </div>

            <div className="ilr-modal-foot">
              <button
                type="button"
                className="ilr-btn-secondary"
                onClick={() => setIsDisburseModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ilr-btn-disburse-confirm"
                onClick={handleConfirmDisburse}
              >
                Confirm Disbursement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstantLoanReport;